<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";

const textbookPoints = [
  "Textbook unit AI ke benefits aur risks dono ko society, education, healthcare, finance aur relationships ke context me discuss karta hai.",
  "Chapter privacy, bias, accountability, job displacement aur social isolation jese ethical issues ko central concern banata hai.",
  "Exercise section me open-ended questions ke sath 5 MCQs bhi di gayi hain jinhen board-style reasoning ke sath samajhna useful hai.",
];

const chapterCoverage = [
  "AI in education",
  "AI in healthcare",
  "AI in finance",
  "Creativity and human limits",
  "Job displacement and new jobs",
  "Relationships and emotions",
  "Ethics, privacy, bias, and accountability",
];

const quickRevisionPoints = [
  "AI benefits aur risks dono ko sath discuss karna chapter ka core method hai.",
  "Education me AI personalization aur assessment me help karti hai.",
  "Healthcare me diagnostic support major example hai.",
  "Finance me fraud detection aur automated systems AI uses hain.",
  "Job displacement ke sath reskilling aur new roles ka issue bhi aata hai.",
  "Ethics me privacy, bias, accountability aur human oversight central hain.",
];

const answerBuilding = [
  "Balanced answer me benefits aur risks dono mention karo; one-sided answer weak hota hai.",
  "Job-loss question me new jobs aur retraining dono likho.",
  "Relationships wale answer me convenience aur isolation ko sath compare karo.",
  "Ethics question me privacy, fairness, transparency aur accountability ko ek group ke tor par likhna useful hota hai.",
];

const textbookExerciseMap = [
  "Textbook exercise me 8 open-ended Reading and Critical Thinking questions hain jo creativity, jobs, emotions, ethics, teachers, long-term consequences aur relationships ko cover karti hain.",
  "Usi exercise block ke sath 5 MCQs di gayi hain jo job displacement, healthcare, education, finance aur communication-tools risk par focused hain.",
  "Chapter ke follow-up me grammar / writing style prompts bhi aati hain, is liye sirf content yaad karna enough nahi; applied answer framing bhi zaroori hoti hai.",
];

const textbookFollowUps = [
  "Education answer me AI as assistant likhna stronger hai; full replacement likhna textbook tone ke against jata hai.",
  "Ethics section me privacy, fairness, transparency, accountability aur human oversight ko ek complete set ke tor par yaad rakhna useful hai.",
  "Long-answer me healthcare, education, finance aur relationships ke examples alag headings me dene se response board-style lagta hai.",
];

const readingQuestions = [
  {
    question: "Can AI replicate true human creativity? What aspects of creativity might AI struggle with?",
    answer: "AI patterns imitate kar sakti hai aur content generate bhi kar sakti hai, lekin true human creativity me lived experience, deep emotion, moral imagination aur original intention ka role hota hai. Yeh aspects AI ke liye naturally difficult rehte hain.",
  },
  {
    question: "How can society balance AI-driven job loss with new job opportunities? What new jobs might AI create?",
    answer: "Society ko reskilling, digital training aur policy support dena hoga taki workers new roles me shift kar saken. AI ethics, data analysis, AI maintenance, prompt design, model auditing aur human oversight jese naye jobs create ho sakte hain.",
  },
  {
    question: "How might AI affect human emotions and social interactions? Can it improve or harm our well-being?",
    answer: "AI communication aur support tools convenience de sakte hain, lekin overreliance social isolation aur real human bonding ko weak kar sakti hai. Is liye impact dono tarah ka ho sakta hai: beneficial bhi aur harmful bhi.",
  },
  {
    question: "What ethical guidelines should govern AI use? Who is responsible for ensuring these standards?",
    answer: "Privacy protection, fairness, transparency, accountability aur human safety AI use ke core principles hone chahiye. In standards ko ensure karne ki responsibility developers, companies, institutions aur governments sab par jointly aati hai.",
  },
  {
    question: "Can AI replace human teachers? What limitations does AI have in education?",
    answer: "AI personalised lessons aur assessment me help kar sakti hai, magar woh empathy, moral guidance, classroom understanding aur human encouragement ko fully replace nahi kar sakti. Isi liye AI teacher ka assistant ho sakti hai, full substitute nahi.",
  },
  {
    question: "What might be some long-term consequences if AI continues to replace human workers in various sectors? How should society address these challenges?",
    answer: "Long-term consequences me unemployment, inequality aur skill gaps barh sakte hain. In challenges ko tackle karne ke liye retraining, social safety measures aur fair economic planning ki zarurat hogi.",
  },
  {
    question: "What are the long-term effects of AI on society? Will humans and AI coexist or will AI surpass human capabilities?",
    answer: "Long term me AI productivity aur convenience ko increase karegi, lekin social and ethical risks bhi sath laye gi. Zyada balanced view yeh hai ke humans aur AI coexist karenge, magar final control aur ethical judgment insano ke paas rehni chahiye.",
  },
  {
    question: "What will be the impact of AI on human relationships? Can AI truly understand emotions?",
    answer: "AI human relationships ko mediate aur influence kar sakti hai, lekin woh emotions ko mostly data patterns ke through detect karti hai, genuinely feel nahi karti. Is liye woh emotional understanding ko imitate kar sakti hai, fully live nahi kar sakti.",
  },
];

const mcqs = [
  {
    question: "What is one of the main concerns associated with the widespread use of AI?",
    options: [
      { label: "a", text: "improved job security", verdict: "incorrect", reason: "Textbook concern iska opposite hai; AI se job security improve hona main concern nahi bataya gaya." },
      { label: "b", text: "job displacement", verdict: "correct", reason: "Chapter explicitly AI-driven job loss aur worker replacement ko major concern ke tor par discuss karta hai." },
      { label: "c", text: "increased social isolation", verdict: "incorrect", reason: "Yeh bhi chapter ka concern hai, lekin is question me textbook ka primary concern job displacement par focus karta hai." },
      { label: "d", text: "reduced healthcare costs", verdict: "incorrect", reason: "Yeh concern nahi balki possible benefit side ka point hai." },
    ],
  },
  {
    question: "Which of the following is an example of AI being used in healthcare?",
    options: [
      { label: "a", text: "automated car driving", verdict: "incorrect", reason: "Yeh transport sector ki example hai, healthcare ki nahi." },
      { label: "b", text: "personalised learning platforms", verdict: "incorrect", reason: "Yeh education sector se relate karti hai." },
      { label: "c", text: "AI-powered diagnostic tools like IBM's Watson for Health", verdict: "correct", reason: "Textbook healthcare example ke tor par diagnostic tools ko mention karta hai." },
      { label: "d", text: "social media content recommendations", verdict: "incorrect", reason: "Yeh media/platform recommendation systems ki example hai, healthcare nahi." },
    ],
  },
  {
    question: "How does AI benefit the education sector?",
    options: [
      { label: "a", text: "by replacing teachers entirely", verdict: "incorrect", reason: "Textbook is idea ko reject karta hai; AI support tool hai, total replacement nahi." },
      { label: "b", text: "by providing personalised lessons and automated assessments", verdict: "correct", reason: "Yeh chapter ka direct education benefit hai." },
      { label: "c", text: "by creating virtual classmates", verdict: "incorrect", reason: "Yeh textbook benefit point nahi hai." },
      { label: "d", text: "by limiting student-teacher interactions", verdict: "incorrect", reason: "Yeh benefit nahi; yeh to educational relationship ko damage kar sakta hai." },
    ],
  },
  {
    question: "In what way does AI enhance the financial industry?",
    options: [
      { label: "a", text: "by replacing all human workers", verdict: "incorrect", reason: "Textbook AI ko total human replacement ke tor par present nahi karta." },
      { label: "b", text: "through fraud detection, customer service chatbots, and automated trading", verdict: "correct", reason: "Yeh chapter me finance sector ke exact practical AI uses hain." },
      { label: "c", text: "by making transactions slower and less accurate", verdict: "incorrect", reason: "AI ka role efficiency aur accuracy improve karna hai, slow karna nahi." },
      { label: "d", text: "by reducing the need for data analysis", verdict: "incorrect", reason: "Finance me AI khud data analysis ko heavily use karti hai." },
    ],
  },
  {
    question: "What is a potential risk of excessive reliance on AI-powered communication tools?",
    options: [
      { label: "a", text: "increased face-to-face interaction", verdict: "incorrect", reason: "Excessive reliance usually face-to-face interaction ko kam karti hai, barhati nahi." },
      { label: "b", text: "enhanced emotional intelligence", verdict: "incorrect", reason: "Yeh guaranteed outcome nahi; chapter risk ke tor par emotional weakening ki taraf ishara karta hai." },
      { label: "c", text: "social isolation and decline in human relationships", verdict: "correct", reason: "Textbook excessive AI communication reliance ka yahi major risk identify karta hai." },
      { label: "d", text: "better quality of human communication", verdict: "incorrect", reason: "Question risk pooch raha hai, benefit nahi." },
    ],
  },
];

usePageSeo({
  title: "The Impact of AI on Society, Human Relationships, and Ethics Notes | 11th Class English Unit 12",
  description: "11th class English Unit 12 AI notes with reading and critical thinking answers, ethics, jobs, relationships, 5 MCQs and option explanations.",
  keywords: "impact of ai on society human relationships and ethics notes, 11th class english unit 12, class 11 english ai chapter, ai short questions, ai mcqs with answers",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Prose", item: "/11th/english/prose/" },
    { name: "The Impact of AI on Society, Human Relationships, and Ethics", item: "/11th/english/prose/the-impact-of-ai-on-society-human-relationships-and-ethics/" },
  ],
});
</script>

<template>
  <PageContainer>
    <section class="app-page-stack">
      <header class="app-page-header">
        <p class="app-page-kicker">Unit 12</p>
        <h1 class="app-page-title">The Impact of AI on Society, Human Relationships, and Ethics</h1>
        <p class="app-page-intro">
          Yeh page textbook ke reading and critical thinking section par based hai. Is me open-ended answers ke sath MCQ option reasoning bhi di gayi hai.
        </p>
      </header>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Textbook-Aligned Snapshot</h2>
        <ul class="app-list">
          <li v-for="point in textbookPoints" :key="point">{{ point }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Unit 12 Coverage Checklist</h2>
        <ul class="app-list-grid">
          <li v-for="item in chapterCoverage" :key="item" class="english-mini-card-muted">
            {{ item }}
          </li>
        </ul>
      </section>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Quick Revision Points</h2>
        <ul class="app-list">
          <li v-for="item in quickRevisionPoints" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Textbook Exercise Map</h2>
        <ul class="app-list">
          <li v-for="item in textbookExerciseMap" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Reading and Critical Thinking Answers</h2>
        <div class="mt-4 space-y-4">
          <article
            v-for="item in readingQuestions"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <p class="app-rich-text mt-3">{{ item.answer }}</p>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">MCQs with Option Explanations</h2>
        <div class="mt-4 space-y-5">
          <article
            v-for="item in mcqs"
            :key="item.question"
            class="english-inner-card-muted"
          >
            <h3 class="app-title-card">{{ item.question }}</h3>
            <ul class="mt-3 space-y-2 text-sm leading-6 text-slate-700 dark:text-slate-300">
              <li v-for="option in item.options" :key="`${item.question}-${option.label}`">
                <span class="font-medium text-slate-900">{{ option.label }}. {{ option.text }}</span>
                <span>
                  {{ option.verdict === "correct" ? " Correct:" : " Not correct:" }}
                  {{ option.reason }}
                </span>
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="english-panel">
        <h2 class="app-section-heading">Answer-Building Notes</h2>
        <ul class="app-list">
          <li v-for="item in answerBuilding" :key="item">{{ item }}</li>
        </ul>
      </section>

      <section class="english-panel-muted">
        <h2 class="app-section-heading">Source-Backed Follow-Up Focus</h2>
        <ul class="app-list">
          <li v-for="item in textbookFollowUps" :key="item">{{ item }}</li>
        </ul>
      </section>
    </section>
  </PageContainer>
</template>





